import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

@Component({
    selector: 'om-aoi-tool',
    templateUrl: './aoi-tool.component.html',
    styleUrls: ['./aoi-tool.component.css']
})
export class AoiToolComponent implements OnInit {
    @ViewChild('stateTab', { static: true }) private readonly stateTab: ElementRef<any>;
    @ViewChild('regionTab', { static: true }) private readonly regionTab: ElementRef<any>;
    @ViewChild('routeTab', { static: true }) private readonly routeTab: ElementRef<any>;
    @ViewChild('assetIdTab', { static: true }) private readonly assetIdTab: ElementRef<any>;
    @ViewChild('addressTab', { static: true }) private readonly addressTab: ElementRef<any>;
    @ViewChild('latLongTab', { static: true }) private readonly latLongTab: ElementRef<any>;
    @ViewChild('intersectionTab', { static: true }) private readonly intersectionTab: ElementRef<any>;
    @ViewChild('mapPicker', { static: true }) private readonly mapPicker: ElementRef<any>;
    @ViewChild('tabs', { static: true }) private readonly tabs: ElementRef<any>;

    @Input() mapRef: any;

    @Output() readonly done: EventEmitter<boolean> = new EventEmitter();

    @Input() loggedIn = true;
    @Output() readonly shouldLogIn: EventEmitter<MouseEvent> = new EventEmitter();

    isLoading: boolean;

    hasExistingLayers: boolean;
    useExistingMap: boolean;

    aoiTabs: any = [];
    activeTab: any;
    aoiService: any;
    layerService: any;
    mapService: any;

    hasSelection: boolean = false;

    assetIdConfig = [
        {
            columnName: 'HPMS_SAMP_SECT_NO',
            displayName: 'HPMS Sample IDs',
            idType: '$stringIds',
            propName: 'samples',
            fieldName: 'sampleId',
            layerId: 2000
        },
        {
            columnName: 'BRKEY',
            displayName: 'Bridge Keys',
            idType: '$stringIds',
            propName: 'bridges',
            fieldName: 'brKey',
            layerId: 1592
        },
        {
            columnName: 'PERMIT_NUM',
            displayName: 'Permit Numbers',
            idType: '$stringIds',
            propName: 'permitNums',
            fieldName: 'permitNum',
            layerId: 9144
        },
        {
            columnName: 'APPLICATION_ID',
            displayName: 'Permit Applications',
            idType: '$stringIds',
            propName: 'permitApps',
            fieldName: 'permitApp',
            layerId: 9144
        },
        {
            columnName: 'PROJECT_ID',
            displayName: 'MPMS Projects',
            idType: '$stringIds',
            propName: 'mpmsprojects',
            fieldName: 'id',
            layerId: 2751
        },
        {
            columnName: 'TMS_SITE_NO',
            displayName: 'TMS Site Numbers',
            idType: '$stringIds',
            propName: 'tmsSite',
            fieldName: 'tmsSiteNo',
            layerId: 2611
        },
        {
            columnName: 'SIGN_APPL_ID',
            displayName: 'Billboard IDs',
            idType: '$stringIds',
            propName: 'billboards',
            fieldName: 'bilboardId',
            layerId: 6763
        }
    ];

    mapPickerConfig = {
        category: 'Planning',
        map: 'TIP Map'
    };

    assetIdProperties = 'samples,bridges,permitNums,permitApps,mpmsprojects,tmsSite,billboards';

    ngOnInit() {

        this.aoiTabs = [
            { name: 'State', elementRef: this.stateTab },
            { name: 'Region', elementRef: this.regionTab, active: true  },
            { name: 'Route', elementRef: this.routeTab },
            { name: 'Asset Id', elementRef: this.assetIdTab },
            { name: 'Address', elementRef: this.addressTab },
            { name: 'Lat/Long', elementRef: this.latLongTab },
            { name: 'Intersection', elementRef: this.intersectionTab }
        ];

        this.aoiTabs.forEach(tab => tab.elementRef.nativeElement.addEventListener('hasSelectionChange', event => this.hasSelection = event.detail));

        this.aoiService = this.stateTab.nativeElement.AoiService;
        this.layerService = this.mapRef.LayerService;
        this.mapService = this.mapRef.MapService;

        this.activeTab = this.regionTab;
        this.assetIdTab.nativeElement.config = this.assetIdConfig;
        this.assetIdTab.nativeElement.properties = this.assetIdProperties;

        this.mapPicker.nativeElement.config = this.mapPickerConfig;

        this.layerService.mapLayersSub.subscribe(layers => {
            if (layers) {
                const mapLayers = (Object.values(layers) as any).flat().filter(l => !this.layerService.isTempLayer(l));
                this.hasExistingLayers = mapLayers.length > 0;
                this.useExistingMap = this.hasExistingLayers;
            }
        });
    }

    activateTab() {
        this.activeTab.nativeElement.activate();
    }

    onTabChange(event) {
        if (event.detail && event.detail.name) {
            this.aoiService.clearSearchCriterias();
            this.activeTab = this.aoiTabs.find(tab => tab.name === event.detail.name).elementRef;
            this.activateTab();
        }
    }

    private async loadMap() {
        const map = this.getMapFromPicker();
        if (map) {
            await this.mapService.openMap(map, true);
        } else {
            await this.mapService.resetMap();
        }
    }

    public async generateMap() {
        this.closeAoiTool();
        try {
            const visibleLayerIds = this.getVisibleLayerIdsFromMap();
            if (!this.useExistingMap) {
                await this.loadMap();
            }
            this.activeTab.nativeElement.addResultToMap();
            await this.aoiService.showBySearchCriterias();
            if (visibleLayerIds.length > 0) {
                this.aoiService.turnOnLayerIds(visibleLayerIds);
            }
        } catch (error) {
            console.error(error);
        }
    }

    getMapFromPicker() {
        const map = this.mapPicker.nativeElement.map;
        if (map && map.data) {
            return map;
        }
        return null;
    }

    getMapData() {
        const map = this.getMapFromPicker();
        if (map && map.data) {
            return JSON.parse(map.data);
        }
        return null;
    }

    getVisibleLayerIdsFromMap() {
        const mapData = this.getMapData();
        if (mapData && mapData.layers) {
            return mapData.layers.filter(x => x.id && x.visible).map(x => x.id);
        }
        return [];
    }

    closeAoiTool() {
        this.done.emit(true);
    }

    login(e: MouseEvent) {
        this.shouldLogIn.emit(e);
    }

    public reset() {
        this.aoiTabs.forEach(tab => {
            if (tab.elementRef.nativeElement.reset) {
                tab.elementRef.nativeElement.reset();
            }
        });
        this.tabs.nativeElement.activateTab('Region');
        this.mapPicker.nativeElement.config = this.mapPickerConfig;
    }
}
